'use client';

import { c as _c } from "react/compiler-runtime";
import { translateInline } from '@/globals/translate/translateInline';
import { IconLinkContent } from '@/uiComposites/interactive/IconLink';
import { closeIcon } from '@/uiIcons/closeIcon.css';
import { menuIcon } from '@/uiIcons/menuIcon.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { focusBox } from '@/uiPrimitives/base/focus.css';
import { box } from '@/uiPrimitives/layout/box';
import { pile } from '@/uiPrimitives/layout/pile';
import { icon } from '@/uiPrimitives/media/icon';
import { ResponsiveMenuType, useMainMenuOpened } from './MainMenuContext';
import { mainMenuMeasurements, searchLinkText } from './mainMenuMeasurements';
import { hiddenForHamburgerMenu, hiddenForNavBarMenu, menuButton, submenu, submenuButton, submenuButtonClosed, submenuContent, menuItems, menu, menuItemsList, menuItemsScroll, submenuMore, submenuScroll, submenuFlyout, flyoutBackdrop, isOverflowingCSS } from './MainMenuStyle.css';
import { ReactNode, createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { hidden } from '@/uiPrimitives/utility/display.css';
import { placement } from '@/uiPrimitives/layout/placement.css';
import { MainMenuWebAPIData } from './MainMenu.types';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { position } from '@/uiPrimitives/layout/position';
import { useLastSearchURL } from '@/sections/search/useSearchHistory';
import { DOPLink } from '@/uiComposites/interactive/DOPLink';
import { searchIcon } from '@/uiIcons/searchIcon.css';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { pushMainMenuEvent } from '@/globals/analytics/events/pushMainMenuEvent';
import { assertNever } from '@/globals/helpers/assert.helpers';
import { searchPath } from '@/globals/config/localization.config';
const FlyoutIDContext = createContext<{
  id: string;
  title: string;
} | undefined>(undefined);
const useFlyoutID = () => {
  const context = useContext(FlyoutIDContext);
  if (context == null) {
    throw new Error("useFlyoutID must be used within a FlyoutIDProvider");
  }
  return context;
};
export const useIsInertBecauseOfMenu = () => {
  const $ = _c(2);
  const [openedFlyout] = useMainMenuOpened();
  switch (openedFlyout.menuType) {
    case "navBar":
      {
        return openedFlyout.submenu != null;
      }
    case undefined:
    case "hamburger":
      {
        return openedFlyout.mainMenu || openedFlyout.submenu != null;
      }
    default:
      {
        let t0;
        if ($[0] !== openedFlyout.menuType) {
          t0 = assertNever(openedFlyout.menuType);
          $[0] = openedFlyout.menuType;
          $[1] = t0;
        } else {
          t0 = $[1];
        }
        return t0;
      }
  }
};
export const UpdateMainMenuBreakpoint = t0 => {
  const $ = _c(4);
  const {
    breakpoint
  } = t0;
  const [, dispatchOpenedMenu] = useMainMenuOpened();
  let t1;
  let t2;
  if ($[0] !== breakpoint || $[1] !== dispatchOpenedMenu) {
    t1 = () => {
      const mediaQuery = window.matchMedia(`(width > ${breakpoint}px)`);
      dispatchOpenedMenu({
        type: "menuType",
        menuType: mediaQuery.matches ? "navBar" : "hamburger"
      });
      const abortController = new AbortController();
      mediaQuery.addEventListener("change", event => {
        dispatchOpenedMenu({
          type: "menuType",
          menuType: event.matches ? "navBar" : "hamburger"
        });
      }, {
        signal: abortController.signal
      });
      return () => {
        abortController.abort();
      };
    };
    t2 = [breakpoint, dispatchOpenedMenu];
    $[0] = breakpoint;
    $[1] = dispatchOpenedMenu;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
};
const useIsOverflowing = () => {
  const $ = _c(4);
  const scrollRef = useRef(null);
  const measureRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const measureElement = measureRef.current;
      const scrollElement = scrollRef.current;
      if (measureElement == null) {
        return;
      }
      if (scrollElement == null) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        setIsOverflowing(!entries[0].isIntersecting);
      }, {
        root: scrollElement,
        threshold: 1
      });
      observer.observe(measureElement);
      return () => {
        observer.disconnect();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== isOverflowing) {
    t2 = {
      scrollRef,
      measureRef,
      isOverflowing
    };
    $[2] = isOverflowing;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export const Menu = t0 => {
  const $ = _c(9);
  const {
    children
  } = t0;
  const mainMenuID = useId();
  let t1;
  if ($[0] !== mainMenuID) {
    t1 = {
      id: mainMenuID,
      title: "mainMenu"
    };
    $[0] = mainMenuID;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = translateInline({
      en: "Main menu",
      nl: "Hoofdmenu"
    });
    t3 = classes(menu, shelf({
      columnGap: mainMenuMeasurements.columnGap,
      alignItems: "stretch",
      justifyContent: "stretch",
      gridTemplateColumns: "minmax(0, 1fr) auto"
    }));
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children) {
    t4 = <nav tabIndex={-1} aria-label={t2} className={t3}>{children}</nav>;
    $[4] = children;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4) {
    t5 = <FlyoutIDContext.Provider value={t1}>{t4}</FlyoutIDContext.Provider>;
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export const MenuButton = () => {
  const $ = _c(26);
  const {
    id
  } = useFlyoutID();
  const [openedFlyout, dispatchOpenedFlyout] = useMainMenuOpened();
  const t0 = `button-${id}`;
  const t1 = openedFlyout.mainMenu;
  const t2 = `flyout-${id}`;
  let t3;
  if ($[0] !== dispatchOpenedFlyout || $[1] !== openedFlyout.mainMenu) {
    t3 = () => {
      if (openedFlyout.mainMenu) {
        dispatchOpenedFlyout({
          type: "closeMainMenu"
        });
        pushMainMenuEvent({
          action: "closeMenu"
        });
      } else {
        dispatchOpenedFlyout({
          type: "openMainMenu"
        });
        pushMainMenuEvent({
          action: "openMenu"
        });
      }
    };
    $[0] = dispatchOpenedFlyout;
    $[1] = openedFlyout.mainMenu;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== openedFlyout.mainMenu) {
    t4 = classes(menuButton, hiddenForNavBarMenu, pile({
      alignItems: "center"
    }), focusBox, box({
      backgroundColor: openedFlyout.mainMenu ? "negative" : "positiveProjectBrand",
      paddingInline: "negative 0 | p"
    }), textStyle({
      fontWeight: "bold"
    }), placement({
      justifySelf: "start"
    }));
    $[3] = openedFlyout.mainMenu;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = addTestSelector("uiMenuButton");
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== openedFlyout.mainMenu) {
    t6 = openedFlyout.mainMenu ? translateInline({
      en: "Close",
      nl: "Sluit"
    }) : translateInline({
      en: "Open",
      nl: "Open"
    });
    $[6] = openedFlyout.mainMenu;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== openedFlyout.mainMenu) {
    t7 = classes(openedFlyout.mainMenu ? icon({
      icon: closeIcon,
      blockSize: "3 | h1"
    }) : icon({
      icon: menuIcon,
      blockSize: "2 | h2"
    }));
    $[8] = openedFlyout.mainMenu;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <i role="img" aria-label={t6} className={t7} />;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = openedFlyout.mainMenu ? "positive:noHover" : "negativeForBrandColor";
  let t10;
  if ($[13] !== t9) {
    t10 = {
      color: t9,
      iconSpace: mainMenuMeasurements.iconOccupy
    };
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = translateInline({
      en: "Menu",
      nl: "Menu"
    });
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t8) {
    t12 = <IconLinkContent icon={t8} styling={t10}>{t11}</IconLinkContent>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== openedFlyout.mainMenu || $[20] !== t0 || $[21] !== t12 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4) {
    t13 = <button id={t0} aria-expanded={t1} aria-controls={t2} onClick={t3} className={t4} {...t5}>{t12}</button>;
    $[19] = openedFlyout.mainMenu;
    $[20] = t0;
    $[21] = t12;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
};
export const MenuItems = t0 => {
  const $ = _c(15);
  const {
    children
  } = t0;
  const {
    id
  } = useFlyoutID();
  const [openedFlyout] = useMainMenuOpened();
  const {
    isOverflowing,
    measureRef,
    scrollRef
  } = useIsOverflowing();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classes(menuItems);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `flyout-${id}`;
  const t3 = !openedFlyout.mainMenu && hiddenForHamburgerMenu;
  const t4 = isOverflowing && isOverflowingCSS;
  let t5;
  if ($[1] !== t3 || $[2] !== t4) {
    t5 = classes(menuItemsScroll, focusBox, t3, t4);
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = classes(menuItemsList);
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== children || $[6] !== measureRef) {
    t7 = <ul ref={measureRef} className={t6}>{children}</ul>;
    $[5] = children;
    $[6] = measureRef;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== scrollRef || $[9] !== t5 || $[10] !== t7) {
    t8 = <div ref={scrollRef} className={t5} tabIndex={0}>{t7}</div>;
    $[8] = scrollRef;
    $[9] = t5;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t2 || $[13] !== t8) {
    t9 = <div className={t1} id={t2}>{t8}</div>;
    $[12] = t2;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
};
export const Submenu = t0 => {
  const $ = _c(11);
  const {
    type,
    title,
    children
  } = t0;
  const id = useId();
  let t1;
  if ($[0] !== id || $[1] !== title) {
    t1 = {
      id,
      title
    };
    $[0] = id;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = type === "more" && submenuMore;
  let t3;
  if ($[3] !== t2) {
    t3 = classes(submenu, t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== t3) {
    t4 = <li className={t3}>{children}</li>;
    $[5] = children;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t4) {
    t5 = <FlyoutIDContext.Provider value={t1}>{t4}</FlyoutIDContext.Provider>;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
const getMenuType = (breakpoint: number) => {
  const menuType: ResponsiveMenuType = window.matchMedia(`(width > ${breakpoint}px)`).matches ? 'navBar' : 'hamburger';
  return menuType;
};
const useClickAndHover = t0 => {
  const $ = _c(15);
  const {
    breakpoint,
    isOpened,
    isAnyOpened,
    onClick,
    onHover
  } = t0;
  const timerRef = useRef(undefined);
  const protectHoverTimerRef = useRef(undefined);
  const protectHoverRef = useRef(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      clearTimeout(timerRef.current);
      clearTimeout(protectHoverTimerRef.current);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== breakpoint || $[3] !== isOpened || $[4] !== onClick) {
    t3 = () => {
      if (protectHoverRef.current && isOpened) {
        return;
      }
      protectHoverRef.current = false;
      clearTimeout(protectHoverTimerRef.current);
      clearTimeout(timerRef.current);
      const menuType = getMenuType(breakpoint);
      onClick(menuType);
    };
    $[2] = breakpoint;
    $[3] = isOpened;
    $[4] = onClick;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      if (event.pointerType !== "mouse") {
        return;
      }
      clearTimeout(timerRef.current);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== breakpoint || $[8] !== isAnyOpened || $[9] !== isOpened || $[10] !== onHover) {
    t5 = event_0 => {
      if (event_0.pointerType !== "mouse") {
        return;
      }
      clearTimeout(timerRef.current);
      const menuType_0 = getMenuType(breakpoint);
      if (menuType_0 === "hamburger") {
        return;
      }
      if (!isAnyOpened) {
        return;
      }
      if (isOpened) {
        return;
      }
      const {
        currentTarget
      } = event_0;
      timerRef.current = setTimeout(() => {
        if (!currentTarget.matches(":hover")) {
          return;
        }
        const menuType_1 = getMenuType(breakpoint);
        if (menuType_1 === "hamburger") {
          return;
        }
        onHover(menuType_1);
        protectHoverRef.current = true;
        clearTimeout(protectHoverTimerRef.current);
        protectHoverTimerRef.current = setTimeout(() => {
          protectHoverRef.current = false;
        }, 400);
      }, 20);
    };
    $[7] = breakpoint;
    $[8] = isAnyOpened;
    $[9] = isOpened;
    $[10] = onHover;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t5) {
    t6 = {
      onClick: t3,
      onPointerLeave: t4,
      onPointerMove: t5
    };
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
export const SubmenuButton = t0 => {
  const $ = _c(43);
  const {
    breakpoint,
    children
  } = t0;
  const {
    id,
    title
  } = useFlyoutID();
  const [openedFlyout, dispatchOpenedFlyout] = useMainMenuOpened();
  const t1 = openedFlyout.submenu === id;
  const t2 = openedFlyout.submenu != null;
  let t3;
  if ($[0] !== dispatchOpenedFlyout || $[1] !== id || $[2] !== openedFlyout.submenu || $[3] !== title) {
    t3 = menuType => {
      if (openedFlyout.submenu === id) {
        dispatchOpenedFlyout({
          type: "closeSubmenu",
          submenuID: id,
          menuType
        });
        pushMainMenuEvent({
          action: "closeSubmenu",
          title
        });
      } else {
        dispatchOpenedFlyout({
          type: "openSubmenu",
          submenuID: id,
          menuType
        });
        pushMainMenuEvent({
          action: "openSubmenu",
          title
        });
      }
    };
    $[0] = dispatchOpenedFlyout;
    $[1] = id;
    $[2] = openedFlyout.submenu;
    $[3] = title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== dispatchOpenedFlyout || $[6] !== id || $[7] !== title) {
    t4 = menuType_0 => {
      dispatchOpenedFlyout({
        type: "openSubmenu",
        submenuID: id,
        menuType: menuType_0
      });
      pushMainMenuEvent({
        action: "openSubmenu",
        title
      });
    };
    $[5] = dispatchOpenedFlyout;
    $[6] = id;
    $[7] = title;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== breakpoint || $[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      breakpoint,
      isOpened: t1,
      isAnyOpened: t2,
      onClick: t3,
      onHover: t4
    };
    $[9] = breakpoint;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const buttonEventProps = useClickAndHover(t5);
  const isOpened = openedFlyout.submenu === id;
  const t6 = `button-${id}`;
  const t7 = `flyout-${id}`;
  const t8 = !isOpened && submenuButtonClosed;
  let t9;
  if ($[15] !== t8) {
    t9 = classes(submenuButton, t8, shelf({
      alignContent: "center",
      alignItems: "start",
      gridTemplateColumns: "minmax(0, 1fr) auto"
    }), focusBox);
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = `uiSubmenuButton-${children}`;
  let t11;
  if ($[17] !== t10) {
    t11 = addTestSelector(t10);
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = classes(position({
      position: "absolute"
    }));
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== isOpened) {
    t13 = isOpened ? translateInline({
      en: "Close",
      nl: "Sluit"
    }) : translateInline({
      en: "Open",
      nl: "Open"
    });
    $[20] = isOpened;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13) {
    t14 = <i className={t12} role="img" aria-label={t13} />;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = classes(textBlock({
      fontWeight: "bold",
      color: "inherit"
    }));
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== children) {
    t16 = <div className={t15}>{children}</div>;
    $[25] = children;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  let t18;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = classes(occupy({
      blockSize: "capSize",
      inlineSize: mainMenuMeasurements.chevronOccupy,
      alignItems: "end",
      justifyItems: "end"
    }));
    t18 = <i className={classes(hiddenForNavBarMenu, icon({
      icon: chevronIcon,
      blockSize: "capSize",
      fill: "currentColor"
    }))} />;
    $[27] = t17;
    $[28] = t18;
  } else {
    t17 = $[27];
    t18 = $[28];
  }
  let t19;
  if ($[29] !== isOpened) {
    t19 = classes(hiddenForHamburgerMenu, icon({
      icon: chevronIcon,
      blockSize: "1ex",
      adjustIcon: "rotate90deg",
      flip: isOpened ? "horizontal" : undefined,
      fill: "currentColor"
    }));
    $[29] = isOpened;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t19) {
    t20 = <div className={t17}>{t18}<i className={t19} /></div>;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== buttonEventProps || $[34] !== isOpened || $[35] !== t11 || $[36] !== t14 || $[37] !== t16 || $[38] !== t20 || $[39] !== t6 || $[40] !== t7 || $[41] !== t9) {
    t21 = <button id={t6} aria-expanded={isOpened} aria-controls={t7} {...buttonEventProps} className={t9} {...t11}>{t14}{t16}{t20}</button>;
    $[33] = buttonEventProps;
    $[34] = isOpened;
    $[35] = t11;
    $[36] = t14;
    $[37] = t16;
    $[38] = t20;
    $[39] = t6;
    $[40] = t7;
    $[41] = t9;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  return t21;
};
export const SubmenuContent = t0 => {
  const $ = _c(16);
  const {
    children
  } = t0;
  const {
    id
  } = useFlyoutID();
  const [openedFlyout] = useMainMenuOpened();
  const isOpened = openedFlyout.submenu === id;
  const {
    isOverflowing,
    measureRef,
    scrollRef
  } = useIsOverflowing();
  const t1 = `flyout-${id}`;
  const t2 = `button-${id}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classes(submenuFlyout);
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== isOpened || $[2] !== isOverflowing) {
    t4 = classes(submenuScroll, focusBox, box({
      backgroundColor: "negative"
    }), !isOpened && hidden, isOverflowing && isOverflowingCSS);
    $[1] = isOpened;
    $[2] = isOverflowing;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classes(submenuContent);
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== children || $[6] !== measureRef) {
    t6 = <div ref={measureRef} className={t5}>{children}</div>;
    $[5] = children;
    $[6] = measureRef;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== scrollRef || $[9] !== t4 || $[10] !== t6) {
    t7 = <div ref={scrollRef} tabIndex={0} className={t4}>{t6}</div>;
    $[8] = scrollRef;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t7) {
    t8 = <div id={t1} role="region" aria-labelledby={t2} className={t3}>{t7}</div>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
export const SubmenuBackButton = () => {
  const $ = _c(10);
  const {
    title
  } = useFlyoutID();
  const [openedFlyout, dispatchOpenedFlyout] = useMainMenuOpened();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = classes(hiddenForNavBarMenu, shelf({}), focusBox, box({
      paddingInline: "negative 0 | p",
      paddingBlockStart: "negative 4 | betweenListItems",
      paddingBlockEnd: "negative 1 | h3",
      backgroundColor: "negative"
    }), position({
      position: "sticky",
      insetBlockStart: "0"
    }), textStyle({
      color: "positiveBlue"
    }));
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== dispatchOpenedFlyout || $[2] !== openedFlyout.submenu || $[3] !== title) {
    t1 = () => {
      if (openedFlyout.submenu == null) {
        return;
      }
      dispatchOpenedFlyout({
        type: "closeSubmenu",
        submenuID: openedFlyout.submenu,
        menuType: "hamburger"
      });
      pushMainMenuEvent({
        action: "closeSubmenu",
        title
      });
    };
    $[1] = dispatchOpenedFlyout;
    $[2] = openedFlyout.submenu;
    $[3] = title;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = addTestSelector("uiSubmenuBackButton");
    t3 = <span className={classes(occupy({
      inlineSize: indentSize,
      blockSize: "capSize",
      justifyItems: "start"
    }))}><i className={classes(icon({
        icon: chevronIcon,
        blockSize: "capSize",
        adjustIcon: "flipHorizontal"
      }))} /></span>;
    t4 = <span className={classes(textBlock({}))}>{translateInline({
        en: "Main menu",
        nl: "Hoofdmenu"
      })}</span>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1) {
    t5 = <button className={t0} onClick={t1} {...t2}>{t3}{t4}</button>;
    $[8] = t1;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
export const SearchLink = () => {
  const $ = _c(5);
  const lastSearchURL = useLastSearchURL() ?? searchPath;
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = classes(pile({
      alignItems: "center"
    }), textStyle({
      fontWeight: "bold"
    }), box({
      paddingInline: "negative 0 | p"
    }), focusBox);
    t1 = addTestSelector("uiMainMenuLink-Search");
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <IconLinkContent icon={<i className={classes(icon({
      icon: searchIcon,
      blockSize: "3 | h1"
    }))} />} styling={{
      color: "negativeForBrandColor",
      iconSpace: mainMenuMeasurements.iconOccupy
    }}>{searchLinkText}</IconLinkContent>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== lastSearchURL) {
    t3 = <DOPLink href={lastSearchURL} data-link-type="openSearch" className={t0} {...t1}>{t2}</DOPLink>;
    $[3] = lastSearchURL;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const FlyoutBackdrop = t0 => {
  const $ = _c(9);
  const {
    breakpoint
  } = t0;
  const [openedFlyout, dispatchOpenedFlyout] = useMainMenuOpened();
  let t1;
  if ($[0] !== openedFlyout.submenu) {
    t1 = classes(flyoutBackdrop, zIndex({
      zIndex: "flyoutBackdrop"
    }), hiddenForHamburgerMenu, openedFlyout.submenu == null && hiddenForNavBarMenu);
    $[0] = openedFlyout.submenu;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== breakpoint || $[3] !== dispatchOpenedFlyout || $[4] !== openedFlyout.submenu) {
    t2 = () => {
      const menuType = getMenuType(breakpoint);
      if (menuType === "hamburger") {
        return;
      }
      dispatchOpenedFlyout({
        type: "closeMainMenu"
      });
      if (openedFlyout.submenu != null) {
        pushMainMenuEvent({
          action: "closeMenu"
        });
      } else {
        pushMainMenuEvent({
          action: "closeMenu"
        });
      }
    };
    $[2] = breakpoint;
    $[3] = dispatchOpenedFlyout;
    $[4] = openedFlyout.submenu;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className={t1} onClick={t2} />;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};